'use client';

import { useRouter } from 'next/navigation';
import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { addQueryParameters, removeQueryParameter } from '@/helpers/common';
import { isFavoriteGame } from '@/helpers/game';
import { useGetCommonStore } from '@/store';
import { ActionsPanelDesktop } from './ActionsPanelDesktop';
import { ActionsPanelMobile } from './ActionsPanelMobile';
import type { ActionsPanelProps } from './types';

export const ActionsPanel = ({
  isFullScreen,
  isDemo,
  isMobile,
  currencies,
  onFullScreen,
  gameTitle,
  gameProvider,
  withTournamentBanner,
  isDemoAvailable,
}: PropsWithChildren<ActionsPanelProps>) => {
  const { replace } = useRouter();

  const useCommonStore = useGetCommonStore();

  const { removeFavoriteGame, addFavoriteGame, favoriteGames } = useCommonStore(
    useShallow((state) => ({
      removeFavoriteGame: state.removeFavoriteGame,
      addFavoriteGame: state.addFavoriteGame,
      favoriteGames: state.favoriteGames,
    }))
  );

  const isFavorite = useMemo(
    () => isFavoriteGame(currencies, favoriteGames),
    [currencies, favoriteGames]
  );

  const handleFavorite = useCallback(() => {
    if (isFavorite) {
      removeFavoriteGame(currencies);
    } else {
      addFavoriteGame(currencies);
    }
  }, [isFavorite, removeFavoriteGame, currencies, addFavoriteGame]);

  const handleToggleDemo = useCallback(() => {
    if (isDemo) {
      removeQueryParameter('demo', replace);
    } else {
      addQueryParameters(replace, { demo: 'true' });
    }
  }, [isDemo, replace]);

  return (
    <>
      {isMobile ? (
        <ActionsPanelMobile
          gameProvider={gameProvider}
          gameTitle={gameTitle}
          handleFavorite={handleFavorite}
          handleToggleDemo={handleToggleDemo}
          isDemo={isDemo}
          isDemoAvailable={isDemoAvailable}
          isFavorite={isFavorite}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
          withTournamentBanner={withTournamentBanner}
        />
      ) : (
        <ActionsPanelDesktop
          handleFavorite={handleFavorite}
          handleToggleDemo={handleToggleDemo}
          isDemo={isDemo}
          isDemoAvailable={isDemoAvailable}
          isFavorite={isFavorite}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
          onFullScreen={onFullScreen}
        />
      )}
    </>
  );
};
